import ShopDTO from "@/dto/shop/ShopDTO";
import React, { useEffect } from "react";
import OrderTab from "../../../enum/OrderTab";
import { useGlobalStore } from "@/store/hooks";
import { useOrderManagementStore } from "../../../store/OrderManagementStore";
import { observer } from "mobx-react-lite";
import { Checkbox, Popover } from "antd";
import { ShippingDeadlineShopTypes } from "@/utils/ShopUtils";
import { delay } from "lodash";
import QuickEnum from "@/scenes/OrderManagement/enum/QuickEnum";
import { InfoCircleFilled } from "@ant-design/icons";
import { showDeadlineShippingOrderTabs } from "@/utils/OrderTabUtil";
import { OrderQuickSearchType } from "../SearchPane/QuickField";
import ShopType from "@/enum/ShopType";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import ShopTypeNameMap from "@/map/ShopTypeNameMap";

interface ShippingOverdueCountProps {
    shop: ShopDTO;
    tab: OrderTab;
}

const ShippingOverdueCount: React.FC<ShippingOverdueCountProps> = ({ shop, tab }) => {
    const { shopMapping } = useGlobalStore();
    const shopType = shopMapping.get(shop?.id)?.type;

    const orderManagementStore = useOrderManagementStore();

    const noShippingOverdueCount =
        !orderManagementStore.orderListCount?.shippingOverduePendingShipmentOrderCount &&
        !orderManagementStore.orderListCount?.shippingOverdueOnHoldOrderCount &&
        !orderManagementStore.orderListCount?.shippingOverdueRefundingOrderCount;

    const hideShippingDeadlineCount =
        !showDeadlineShippingOrderTabs.includes(tab) ||
        !ShippingDeadlineShopTypes.includes(shopType) ||
        noShippingOverdueCount;

    useEffect(() => {
        return () => {
            orderManagementStore.table.switchShippingOverdueChecked(false);
        };
    }, [tab]);

    if (hideShippingDeadlineCount) return null;

    function handleChange(e) {
        const checked = e.target.checked;

        let { quicks } = orderManagementStore.table.form.getFieldsValue();

        if (checked) {
            quicks = quicks?.includes(QuickEnum.QUICK_DELIVERY_TIMEOUT)
                ? [QuickEnum.QUICK_DELIVERY_TIMEOUT, QuickEnum.QUICK_SHIPPING_DEADLINE_OVERDUE]
                : [QuickEnum.QUICK_SHIPPING_DEADLINE_OVERDUE];
        } else {
            quicks = quicks?.includes(QuickEnum.QUICK_DELIVERY_TIMEOUT) ? [QuickEnum.QUICK_DELIVERY_TIMEOUT] : [];
        }

        orderManagementStore.table.switchShippingOverdueChecked(checked);
        orderManagementStore.table.resetFormValues();

        delay(() => {
            orderManagementStore.table.form.setFieldsValue({
                quicks,
                orderQuickSearchType: OrderQuickSearchType.AnyMatchQuickCriterion,
            });
            orderManagementStore.table.changePageNo(1);
            orderManagementStore.fetchOrders(shop.id, tab);
        }, 10);

        collectEvent(DataTrackEvent.QUICK_SEARCH_ORDER, {
            shop_id: shop.id,
            shop_name: shop.name,
            shop_type: ShopTypeNameMap[shop.type],
            type: "筛选已超时发货的订单",
        });
    }

    const isPDD = [ShopType.PINDUODUO, ShopType.PDD_FDS].includes(shopType);

    return (
        <Popover
            title={
                <div>
                    <div>
                        <InfoCircleFilled style={{ color: "rgb(17,145,255)" }} />
                        <span className="fb ml10">点击可快速筛选{isPDD ? "已超时发货或揽收" : "已超时发货"}的订单</span>
                    </div>
                    {tab !== OrderTab.PENDING_SHIPMENT &&
                    orderManagementStore.orderListCount?.shippingOverduePendingShipmentOrderCount ? (
                        <div className="error fb ml25">
                            {isPDD ? "超时发货或揽收" : "超时发货"}的待发货订单需到‘待发货’状态列表下处理
                        </div>
                    ) : null}
                    {tab !== OrderTab.ON_HOLD &&
                    orderManagementStore.orderListCount?.shippingOverdueOnHoldOrderCount ? (
                        <div className="error fb ml25">
                            {isPDD ? "超时发货或揽收" : "超时发货"}的挂起订单需到‘挂起’状态列表下处理
                        </div>
                    ) : null}
                    {tab !== OrderTab.PENDING_SHIPMENT_REFUNDING &&
                    orderManagementStore.orderListCount?.shippingOverdueRefundingOrderCount ? (
                        <div className="error fb ml25">
                            {isPDD ? "超时发货或揽收" : "超时发货"}的退款中订单需到‘退款中’状态列表下处理
                        </div>
                    ) : null}
                </div>
            }
        >
            <span className="tc mb5 inline-block shipping-overdue-count-container ml5">
                <Checkbox
                    style={{ backgroundColor: "rgb(193, 0, 16)" }}
                    className="text-white"
                    onChange={handleChange}
                    checked={orderManagementStore.table.shippingOverdueChecked}
                >
                    <span>{isPDD ? "超时发货或揽收" : "超时发货"}:</span>

                    {[
                        {
                            label: "待发货",
                            count: orderManagementStore.orderListCount?.shippingOverduePendingShipmentOrderCount,
                            className: "ml10",
                        },
                        {
                            label: "挂起",
                            count: orderManagementStore.orderListCount?.shippingOverdueOnHoldOrderCount,
                        },
                        {
                            label: "退款中",
                            count: orderManagementStore.orderListCount?.shippingOverdueRefundingOrderCount,
                        },
                    ]
                        .filter((item) => item.count)
                        .map((item, index, array) => (
                            <span key={item.label} className={index === 0 ? "ml10" : ""}>
                                {item.label}
                                {item.count}
                                {index < array.length - 1 && "，"}
                            </span>
                        ))}
                </Checkbox>
            </span>
        </Popover>
    );
};

export default observer(ShippingOverdueCount);
